<template>
  <div class="view active">
    <div class="view-header">
      <h1>Silver <span class="ticker">XAG/USD</span></h1>
      <p class="subtitle">Geocosmic Reversal Context</p>
    </div>

    <div v-if="silverPrice.price" class="price-display">
      <span class="big-price">${{ silverPrice.price?.toLocaleString(undefined, { maximumFractionDigits: 2 }) }}</span>
      <span class="big-change" :class="silverPrice.change >= 0 ? 'up' : 'down'">
        {{ silverPrice.change >= 0 ? '+' : '' }}{{ silverPrice.change?.toFixed(2) }} ({{ silverPrice.changePercent?.toFixed(2) }}%)
      </span>
    </div>

    <div class="chart-section">
      <div class="chart-header">
        <div class="chart-title-stack">
          <h2>Price Chart</h2>
          <p>SLV ETF &bull; {{ rangeLabel }} Performance</p>
        </div>
        <div class="chart-range-switcher">
          <button :class="{ active: chartRange === '7d' }" @click="setRange('7d')">7D</button>
          <button :class="{ active: chartRange === '30d' }" @click="setRange('30d')">30D</button>
          <button :class="{ active: chartRange === '90d' }" @click="setRange('90d')">90D</button>
          <button :class="{ active: chartRange === '1y' }" @click="setRange('1y')">1Y</button>
          <button :class="{ active: chartRange === '5y' }" @click="setRange('5y')">5Y</button>
          <button :class="{ active: chartRange === '10y' }" @click="setRange('10y')">10Y</button>
        </div>
      </div>
      <div class="chart-wrapper">
        <FinanceChart :data="chartPoints" color="#5f6b7a" :price-precision="2" label="SLV" />
      </div>
    </div>

    <div class="overview-research">
      <div class="recommendation-panel">
        <div class="section-header">
          <h2>Timing Windows</h2>
        </div>
        <TimingWindowTable :windows="timingWindows" />
      </div>
    </div>

    <div class="recommendation-panel" style="margin-top: 1rem;">
      <div class="section-header">
        <h2>Validated Silver Strategies</h2>
      </div>
      <p class="section-intro">
        Based on Merriman's "The Sun, the Moon, and Silver Market" and Raghavan's research.
        All strategies validated against SLV ETF (2006-2026) using Swiss Ephemeris sidereal positions.
      </p>
      <div class="strategies-grid">
        <div class="strategy-card">
          <h3 class="strategy-name">S-N Aspect (Non-Toxic)</h3>
          <div class="strategy-stats">
            <span class="stat">Win Rate: <strong class="text-success">70%</strong></span>
            <span class="stat">Expected: <strong class="text-success">+5%</strong></span>
            <span class="stat">Hold: <strong>5 days</strong></span>
          </div>
          <p class="strategy-desc">
            Saturn-Neptune aspects outside toxic zones (avoid 183 degrees, 87 degrees).
            Indicates structural shifts in silver supply/demand dynamics.
          </p>
        </div>

        <div class="strategy-card">
          <h3 class="strategy-name">J-S Aspect (Non-Toxic)</h3>
          <div class="strategy-stats">
            <span class="stat">Win Rate: <strong class="text-success">68%</strong></span>
            <span class="stat">Expected: <strong class="text-success">+4.5%</strong></span>
            <span class="stat">Hold: <strong>7 days</strong></span>
          </div>
          <p class="strategy-desc">
            Jupiter-Saturn aspects outside toxic zones (avoid 93 degrees, 87 degrees).
            Expansion-contraction cycles in precious metals.
          </p>
        </div>

        <div class="strategy-card">
          <h3 class="strategy-name">Mars in Leo</h3>
          <div class="strategy-stats">
            <span class="stat">Win Rate: <strong class="text-success">72%</strong></span>
            <span class="stat">Expected: <strong class="text-success">+5.5%</strong></span>
            <span class="stat">Hold: <strong>5 days</strong></span>
          </div>
          <p class="strategy-desc">
            Mars transit through Leo creates energetic conditions favorable for silver rallies.
            High-conviction short-term signal.
          </p>
        </div>

        <div class="strategy-card">
          <h3 class="strategy-name">Moon in Taurus</h3>
          <div class="strategy-stats">
            <span class="stat">Win Rate: <strong class="text-success">69%</strong></span>
            <span class="stat">Expected: <strong class="text-success">+4%</strong></span>
            <span class="stat">Hold: <strong>2 days</strong></span>
          </div>
          <p class="strategy-desc">
            Moon transit through Taurus (earth sign ruled by Venus) favors precious metals.
            Quick rotational trade with tight risk management.
          </p>
        </div>
      </div>
    </div>

    <div class="intelligence-briefing">
      <span class="briefing-kicker">Subjective Intelligence Report</span>
      <div class="briefing-title">Geocosmic Reversal Context</div>
      <div class="briefing-content">
        Merriman's <strong>Isolated Low Primary Cycle</strong> identifies moments of total retail exhaustion. When Mercury ingress occurs during a Mars in Leo cycle, the "Quick-Strike" probability exceeds 66%.
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMarketStore } from '../stores/market';
import TimingWindowTable from '../components/TimingWindowTable.vue';
import FinanceChart from '../components/FinanceChart.vue';

const marketStore = useMarketStore();
const chartRange = ref('30d');

const silverPrice = computed(() => marketStore.prices.silver || { price: 0, change: 0, changePercent: 0 });

const timingWindows = ref([
  // === APRIL-MAY 2026 ===
  { type: 'BUY', startDate: '2026-04-17', endDate: '2026-04-18', signal: 'Mercury Ingress + Waxing Moon', score: '3.2', expected: '+3-6%', winRate: '83.9%' },
  { type: 'BUY', startDate: '2026-04-24', endDate: '2026-04-27', signal: 'Venus Ingress + Waxing Moon', score: '3.4', expected: '+3-5%', winRate: '76.6%' },
  { type: 'BUY', startDate: '2026-05-07', endDate: '2026-05-10', signal: 'Neptune Station + Moon Aquarius', score: '4.7', expected: '+5-9%', winRate: '78.7%' },
  { type: 'BUY', startDate: '2026-05-17', endDate: '2026-05-21', signal: 'Mercury Ingress + Venus Ingress confluence', score: '4.2', expected: '+5-10%', winRate: '83.9%' },
  { type: 'BUY', startDate: '2026-05-23', endDate: '2026-05-25', signal: 'Double Mutable + Waxing Moon', score: '3.9', expected: '+4-7%', winRate: '77.7%' },
  // === JUNE 2026 ===
  { type: 'BUY', startDate: '2026-06-01', endDate: '2026-06-04', signal: 'Mercury Ingress Cancer + Neptune Station residual + Double Mutable', score: '4.8-6.0', expected: '+4-8%', winRate: '77%' },
  { type: 'BUY', startDate: '2026-06-05', endDate: '2026-06-08', signal: 'Neptune Station + Moon Aquarius Elite', score: '3.5-4.0', expected: '+3-6%', winRate: '78%' },
  { type: 'BUY', startDate: '2026-06-13', endDate: '2026-06-15', signal: 'Venus Ingress Leo + Neptune Station + Double Mutable Gemini', score: '4.0-5.7', expected: '+5-9%', winRate: '76%' },
  { type: 'BUY', startDate: '2026-06-16', endDate: '2026-06-22', signal: 'Waxing Moon + Neptune Station residual', score: '3.0-4.0', expected: '+3-5%', winRate: '75%' },
  { type: 'BUY', startDate: '2026-06-23', endDate: '2026-06-28', signal: 'Double Mutable Gemini+Vir + Neptune Station + Waxing', score: '4.0-6.0', expected: '+4-8%', winRate: '78%' },
]);

const rangeLabel = computed(() => {
  const map = { '7d': '7 Day', '30d': '30 Day', '90d': '90 Day', '1y': '1 Year', '5y': '5 Year', '10y': '10 Year' };
  return map[chartRange.value] || '30 Day';
});

const getDaysForRange = () => {
  switch (chartRange.value) {
    case '7d': return 7;
    case '30d': return 30;
    case '90d': return 90;
    case '1y': return 365;
    case '5y': return 1825;
    case '10y': return 3650;
    default: return 30;
  }
};

const getFetchDaysForRange = () => {
  switch (chartRange.value) {
    case '7d': return 30;
    case '30d': return 90;
    case '90d': return 120;
    case '1y': return 400;
    case '5y': return 1900;
    case '10y': return 3700;
    default: return 90;
  }
};

const setRange = async (range) => {
  chartRange.value = range;
  const fetchDays = getFetchDaysForRange();
  const currentDataLen = marketStore.commodityData.silver?.chartData?.length || 0;
  if (currentDataLen < fetchDays * 0.8) {
    await marketStore.fetchCommodityData('silver', fetchDays);
  }
};

const chartPoints = computed(() => {
  const raw = marketStore.commodityData.silver?.chartData || [];
  if (!raw.length) return [];
  const days = getDaysForRange();
  const sliced = raw.slice(-days);
  return sliced.map(p => ({
    time: p.date,
    value: Number(p.close),
  }));
});

onMounted(async () => {
  await marketStore.fetchCommodityData('silver', 400);
});
</script>

<style scoped>
.intelligence-briefing {
  background: linear-gradient(135deg, rgba(248, 244, 235, 0.98), rgba(255, 255, 255, 0.98));
  border: 1px solid rgba(95, 107, 122, 0.22);
  border-radius: 16px;
  padding: 1.25rem 1.35rem;
  margin-top: 1.5rem;
  box-shadow: 0 4px 12px rgba(95, 107, 122, 0.08);
}

.briefing-kicker {
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-dim);
  margin-bottom: 0.5rem;
}

.briefing-title {
  font-size: 1.08rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.65rem;
  letter-spacing: -0.01em;
}

.briefing-content {
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--text-secondary);
}

.briefing-content strong {
  color: var(--silver);
  font-weight: 600;
}

.chart-title-stack p {
  margin-top: 0.3rem;
  color: var(--text-secondary);
  font-size: 0.82rem;
}

.section-intro {
  color: var(--text-secondary);
  font-size: 0.85rem;
  line-height: 1.6;
  margin-bottom: 1rem;
}

.strategies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.strategy-card {
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 4px 12px rgba(29, 36, 48, 0.06);
  transition: all 0.2s ease;
}

.strategy-card:hover {
  border-color: rgba(95, 107, 122, 0.32);
  box-shadow: 0 6px 18px rgba(95, 107, 122, 0.12);
}

.strategy-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.75rem;
  letter-spacing: -0.01em;
}

.strategy-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.8rem;
}

.stat {
  color: var(--text-secondary);
}

.stat strong {
  color: var(--text-primary);
}

.strategy-desc {
  color: var(--text-secondary);
  font-size: 0.85rem;
  line-height: 1.6;
  margin-bottom: 0.75rem;
}

.text-success { color: var(--bullish); }
.text-warning { color: var(--warning); }
.text-danger { color: var(--danger); }
</style>
